import React from 'react';
import {
  AUTH_USER_TYPE_NORMAL,
  AUTHENTICATION_TYPE,
  DATA_KEYS,
  EVENTS,
  ROUTES,
} from 'shared/constants';
import analyticsService from 'src/helpers/analytics.service';
import { AuthPage } from '../auth-page-wrapper';
import { useFormik } from 'formik';
import styles from './sign-in.module.scss';
import { loginSchema } from 'shared/schemas/user.schemas';
import { TextInput } from 'src/components/design-system/forms';
import { Button } from 'src/components/design-system/buttons';
import { useSelector, useDispatch } from 'react-redux';
import { getLang } from 'shared/store/selectors/lang.selector';
import { getUser } from 'shared/store/selectors/user.selector';
import { clearSignOutCode } from 'shared/store/actions/auth.actions';
import { NavLink, useLocation } from 'react-router-dom';
import { useOauthConfig, useSignInFlow } from '../hooks';
import { navigate } from 'shared/services/navigation.service';
import { getSignInFlowErrorMessage } from 'shared/hooks/auth.hooks';

const { WELCOME_SCREEN } = DATA_KEYS;

export function SignInPage() {
  const [error, setError] = React.useState(null);
  const [loading, setLoadingState] = React.useState(false);
  const { state } = useLocation();
  const { doSignInWithOauth } = useOauthConfig();
  const { startSignIn } = useSignInFlow();
  const dispatch = useDispatch();
  const signOutCode = useSelector(state => state?.auth?.signOutCode || null);

  // Clear signOutCode from Redux when component mounts
  React.useEffect(() => dispatch(clearSignOutCode()), [dispatch]);

  React.useEffect(() => {
    async function signInWithOauth() {
      if (state?.query?.code) {
        setError('');
        setLoadingState(true);

        const res = await doSignInWithOauth(state);

        if (res.error) {
          setError(res.error);
        }

        setLoadingState(false);
      }
    }
    signInWithOauth();
  }, [state, doSignInWithOauth]);

  const lang = useSelector(getLang('WELCOME_SCREEN'));
  const user = useSelector(getUser);

  React.useEffect(() => {
    if (signOutCode) {
      const message = lang[signOutCode];
      if (message) {
        setError(message);
      }
    }
  }, [signOutCode, lang]);

  const signInAction = React.useCallback(
    async ({ email }) => {
      setError('');
      setLoadingState(true);

      const result = await startSignIn({ email });

      if (result.error) {
        analyticsService.track(EVENTS.AUTH.LOGIN_FAILED, {
          error: result.error,
          email,
          signOutCode,
          authType: result.authType,
          entryPoint: 'signin',
        });
        setError(getSignInFlowErrorMessage(result.error, lang));
      } else if (result.authType === AUTHENTICATION_TYPE.OTP) {
        navigate(ROUTES.OTP_VERIFICATION, { email });
      }

      setLoadingState(false);
    },
    [startSignIn, lang, signOutCode],
  );

  const { handleChange, handleBlur, handleSubmit, values, errors, touched, isValid } = useFormik({
    validationSchema: loginSchema,
    initialValues: { email: (user?.type === AUTH_USER_TYPE_NORMAL && user?.email) || '' },
    onSubmit: signInAction,
  });

  return (
    <AuthPage title={lang.HEADER} content={lang.CONTENT}>
      <form onSubmit={handleSubmit}>
        <TextInput
          data-key={WELCOME_SCREEN.EMAIL_INPUT}
          type="email"
          name="email"
          value={values.email}
          error={touched.email && errors.email}
          onChange={handleChange}
          onBlur={handleBlur}
          autoFocus={true}
          size="middle"
          placeholder={lang.EMAIL_PLACEHOLDER}
          disabled={loading}
          status={error ? 'error' : 'default'}
          required
        />

        <span className={styles.error}>{error}</span>

        <Button
          data-key={WELCOME_SCREEN.SUBMIT_BUTTON}
          disabled={!isValid}
          type="primary"
          htmlType="submit"
          loading={loading}>
          {lang.SIGN_IN}
        </Button>

        <p className={styles.notMember}>
          <NavLink to={ROUTES.NOT_ALLOWED} className={styles.link}>
            {lang.NOT_A_MEMBER_CONTACT_US}
          </NavLink>
          {lang.NOT_A_MEMBER}
        </p>
      </form>
    </AuthPage>
  );
}
